import * as React from 'react';
import { Grid } from '@mui/material';
import { FormInput } from '~/components/FormComponents/FormInput';
import { AddressInfo } from '~/modules/identification/components/FormComponents/AddressInfo';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete';
import { useAddressQuery } from '~/queries/useAddressQuery';
import { FormDateInput } from '~/components/FormComponents/FormDateInput';
import { useNewKvedQuery } from '~/queries/useNewKvedQuery';
import { useFormContext } from 'react-hook-form';
import { useGetOwnerIndex } from '../../hooks/useGetOwnerIndex';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { UKRAINE_CODE } from '~/constants/common.ts';
import { useEffect } from 'react';

interface ILegalResidentOwnerFormProps {
  index: number;
  isFirstLevel: boolean;
  handlePercentageChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

export const LegalResidentOwnerForm = React.memo(
  ({ index, isFirstLevel, handlePercentageChange }: ILegalResidentOwnerFormProps) => {
    const { trigger, watch, setValue } = useFormContext();
    const { nonAgressionCountryOptions } = useAddressQuery();
    const { kveds } = useNewKvedQuery();
    const { ownerIndex } = useGetOwnerIndex(index);
    const residenceCountryIsUkraine = watch(`owners.${ownerIndex}.residenceCountry`);

    const handleBlur = () => {
      trigger([
        `owners.${ownerIndex}.identificationNumber`,
        `owners.${ownerIndex}.foreignTaxNumber`,
      ]);
    };

    const isDisabledTypesOfActivity =
      !residenceCountryIsUkraine || residenceCountryIsUkraine?.value === UKRAINE_CODE;

    useEffect(() => {
      if (residenceCountryIsUkraine && residenceCountryIsUkraine?.value !== UKRAINE_CODE) {
        setValue(`owners.${ownerIndex}.typesOfActivity`, null);
      }
    }, [residenceCountryIsUkraine, ownerIndex, setValue]);

    return (
      <Grid container spacing={3}>
        <FormGridItem>
          <FormInput
            name={`owners.${ownerIndex}.fullName`}
            placeholder="Введіть повне найменування"
            label="Повне найменування"
            fullWidth
          />
        </FormGridItem>
        <FormGridItem>
          <FormInput
            name={`owners.${ownerIndex}.shortNameUkr`}
            placeholder="Введіть скорочене найменування"
            label="Скорочене найменування"
            fullWidth
          />
        </FormGridItem>
        <FormGridItem>
          <FormInput
            name={`owners.${ownerIndex}.identificationNumber`}
            onBlur={handleBlur}
            label="Код ЄДРПОУ/ реєстраційний номер (за наявності)"
            placeholder="Введіть код ЄДРПОУ"
            fullWidth
            inputProps={{ maxLength: 9, minLength: 8 }}
          />
        </FormGridItem>
        <FormGridItem>
          <FormInput
            name={`owners.${ownerIndex}.foreignTaxNumber`}
            onBlur={handleBlur}
            label="Реєстраційний номер - для нерезидентів (за наявності)"
            placeholder="Введіть реєстраційний номер - для нерезидентів"
            fullWidth
          />
        </FormGridItem>
        <FormGridItem>
          <FormAutocomplete
            name={`owners.${ownerIndex}.residenceCountry`}
            options={nonAgressionCountryOptions}
            inputProps={{
              placeholder: 'Оберіть країну резидентства',
            }}
            label="Країна резидентства"
            fullWidth
          />
        </FormGridItem>
        {isFirstLevel && (
          <FormGridItem>
            <FormDateInput
              name={`owners.${ownerIndex}.registrationDate`}
              placeholder="Введіть дату державної реєстрації"
              label="Дата державної реєстрації"
              isCurrentMaxDate
            />
          </FormGridItem>
        )}
        {isFirstLevel && (
          <FormGridItem>
            <FormAutocomplete
              options={kveds}
              name={`owners.${ownerIndex}.typesOfActivity`}
              inputProps={{
                placeholder: 'Оберіть основний КВЕД',
              }}
              disabled={!isDisabledTypesOfActivity}
              fullWidth
              label="Основний КВЕД"
            />
          </FormGridItem>
        )}
        <FormGridItem>
          <FormInput
            name={`link.${index}.ownershipPercentage`}
            placeholder="Введіть частку в статутному капіталі"
            label="Частка в статутному капіталі (%)"
            fullWidth
            onChange={handlePercentageChange}
            disableFormOnChange
          />
        </FormGridItem>
        {isFirstLevel ? (
          <AddressInfo section={`owners.${ownerIndex}`} />
        ) : (
          <>
            <FormGridItem>
              <FormAutocomplete
                name={`owners.${ownerIndex}.address.registrationAddress.country`}
                options={nonAgressionCountryOptions}
                inputProps={{
                  placeholder: 'Оберіть країну місця реєстрації',
                }}
                label="Країна місця реєстрації"
                fullWidth
              />
            </FormGridItem>
            <FormGridItem>
              <FormAutocomplete
                name={`owners.${ownerIndex}.address.permanentAddress.country`}
                options={nonAgressionCountryOptions}
                inputProps={{
                  placeholder: 'Оберіть країну фактичного місцезнаходження',
                }}
                label="Країна фактичного місцезнаходження"
                fullWidth
              />
            </FormGridItem>
          </>
        )}
      </Grid>
    );
  }
);
